/**

Coded by www.techhighway.ca

*/

import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import customAlert from "components/MDAlert/customAlert";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSwitch from "components/MDSwitch";

// Material Dashboard 2 React example components
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import WildlifeAuctionService from "services/wildlife-auction-service";

// @mui icons
import { useTheme } from "@emotion/react";
import { Paper } from "@mui/material";
import noImage from "assets/images/no_image.png";
import MDCircularProgress from "components/MDCircularProgress";
import MDDateTimePicker from "components/MDDateTimePicker";
import MDXEditorComponent from "components/MDEditor";
import MDSelect from "components/MDSelect";
import MDTimeTicker from "components/MDTimeTicker";
import MDTypography from "components/MDTypography";
import { useCache } from "context";
import dayjs from "dayjs";
import { sourceOptions } from "utility/constant-dropdown";
import { openCarousel } from "utility/imageCarousel";

const validateInputs = (inputs) => {
  let errors = {};

  // source: in:wildlifebuyer,exoticauction
  if (!inputs.source) {
    errors.sourceError = "Source is required.";
  }

  if (inputs.source && !["wildlifebuyer", "exoticauction"].includes(inputs.source)) {
    errors.sourceError = "Source must be Wildlife Buyer or Exotic Auction";
  }

  // title: nullable|string|max:255
  if (inputs.title && inputs.title.trim().length > 255) {
    errors.titleError = "Title must be at most 255 characters";
  }

  // subtitle: nullable|string|max:255
  if (inputs.subtitle && inputs.subtitle.trim().length > 255) {
    errors.subtitleError = "Subtitle must be at most 255 characters";
  }

  // seller: nullable|string|max:100
  if (inputs.seller && inputs.seller.trim().length > 100) {
    errors.sellerError = "Seller must be at most 100 characters";
  }

  // listing_type: nullable|string|max:50
  if (inputs.listing_type && inputs.listing_type.trim().length > 50) {
    errors.listingTypeError = "Listing type must be at most 50 characters";
  }

  // price: nullable|numeric|min:0|decimal:0,2
  if (inputs.price !== "" && inputs.price !== null) {
    if (isNaN(inputs.price)) {
      errors.priceError = "Price must be a number";
    } else if (Number(inputs.price) < 0) {
      errors.priceError = "Price cannot be negative";
    } else if (!/^\d+(\.\d{1,2})?$/.test(inputs.price)) {
      errors.priceError = "Price can have at most 2 decimal places";
    }
  }

  // listing_id: required|integer
  if (!inputs.listing_id) {
    errors.listingIdError = "Listing ID is required";
  } else if (!Number.isInteger(Number(inputs.listing_id))) {
    errors.listingIdError = "Listing ID must be an integer";
  }

  // listing_url: required|string|max:256
  if (!inputs.listing_url) {
    errors.listingUrlError = "Listing URL is required";
  } else if (inputs.listing_url.trim().length > 256) {
    errors.listingUrlError = "Listing URL must be at most 256 characters";
  } else {
    try {
      new URL(inputs.listing_url);
    } catch {
      errors.listingUrlError = "Invalid URL format";
    }
  }

  // image_url: required|string|max:200
  if (!inputs.image_url) {
    errors.imageUrlError = "Image URL is required";
  } else if (inputs.image_url.trim().length > 200) {
    errors.imageUrlError = "Image URL must be at most 200 characters";
  } else {
    try {
      new URL(inputs.image_url);
    } catch {
      errors.imageUrlError = "Invalid image URL format";
    }
  }

  // category: nullable|string|max:100
  if (inputs.category && inputs.category.trim().length > 100) {
    errors.categoryError = "Category must be at most 100 characters";
  }

  // species: nullable|string|max:100
  if (inputs.species && inputs.species.trim().length > 100) {
    errors.speciesError = "Species must be at most 100 characters";
  }

  // reserve_status: nullable|string|max:100
  if (inputs.reserve_status && inputs.reserve_status.trim().length > 100) {
    errors.reserveStatusError = "Reserve status must be at most 100 characters";
  }

  // status: nullable|string|max:50
  if (inputs.status && inputs.status.trim().length > 50) {
    errors.statusError = "Status must be at most 50 characters";
  }

  return errors;
};


function Form() {
  const theme = useTheme();
  const { action } = useParams();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const navigate = useNavigate();
  const location = useLocation();

  const { openAlertSB, closeAlertSB, renderAlertSB } = customAlert();
  const [active, setActive] = useState(1);
  const [headerTitle, setHeaderTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [descriptionLoading, setDescriptionLoading] = useState(false);
  const [description, setDescription] = useState("");
  const { cacheItems, setCacheItems } = useCache();
  const [isData, setIsData] = useState(false);
  let images = [];
  let image = '';

  const [inputs, setInputs] = useState({
    source: "",
    title: "",
    subtitle: "",
    seller: "",
    listing_type: "",
    price: "",
    listing_id: "",
    listing_url: "",
    image_url: "",
    category: "",
    species: "",
    description: "",
    reserve_status: "",
    remaining_time: "",
    status: "",
    start_date: "",
    end_date: "",
    active: 1,
    male: 0,
    female: 0,
    male_calf: 0,
    female_calf: 0,
    unsexed_calf: 0,
    edited_columns: "",
  });

  const [errors, setErrors] = useState({
    listingIdError: false,
    listingUrlError: false,
    imageUrlError: false,
    titleError: false,
    subtitleError: false,
    sellerError: false,
    listingTypeError: false,
    priceError: false,
    categoryError: false,
    speciesError: false,
    reserveStatusError: false,
    statusError: false,
  });

  useEffect(() => {
    if (action === "add" && id == null) {
      setIsData(true);
      return;
    }

    const showWildlifeAuction = async () => {
      try {
        setDescriptionLoading(true);
        const data = await WildlifeAuctionService.showWildlifeAuction(id);
        setHeaderTitle(data?.listing_id);
        setInputs(data);
        setActive(data?.active);
        setStartDate(data?.start_date);
        setEndDate(data?.end_date);
        setDescription(data?.description);
        setIsData(true);
        setDescriptionLoading(false);
        images = data?.image_url ? data?.image_url.split(",") : [];
        image = images.length > 0 ? images[0] : '';
      } catch (res) {
        setIsData(true);
        setDescriptionLoading(false);
        if (res.hasOwnProperty("message")) {
          const state = { color: "error", icon: "warning", msg: res.message, title: "Auction Listing" };
          navigate("/active-listings", { state });
        } else {
          const state = {
            color: "error",
            icon: "warning",
            msg: res.errors[0].detail,
            title: "Auction Listing",
          };
          navigate("/active-listings", { state });
        }
      }
    };
    showWildlifeAuction();
  }, []);

  const changeHandler = (e) => {
    setLoading(false);
    setInputs({
      ...inputs,
      [e.target.name]: e.target.value,
    });
  };

  const handleEditorChange = (value) => {
    setDescription(value);
  };

  const submitHandler = async (e) => {
    setLoading(true);
    e.preventDefault();

    const validationErrors = validateInputs(inputs);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return; // Stop if errors exist
    }

    const newWildlifeAuction = {
      source: inputs.source,
      title: inputs.title,
      subtitle: inputs.subtitle,
      seller: inputs.seller,
      listing_type: inputs.listing_type,
      price: inputs.price,
      listing_id: inputs.listing_id,
      listing_url: inputs.listing_url,
      image_url: inputs.image_url,
      category: inputs.category,
      species: inputs.species,
      description: description,
      reserve_status: inputs.reserve_status,
      remaining_time: inputs.remaining_time,
      status: inputs.status,
      start_date: startDate,
      end_date: endDate,
      active: active ? 1 : 0,
      male: inputs.male,
      female: inputs.female,
      male_calf: inputs.male_calf,
      female_calf: inputs.female_calf,
      unsexed_calf: inputs.unsexed_calf,
      edited_columns: inputs.edited_columns,
    };

    if (action === "add") {
      try {
        const response = await WildlifeAuctionService.addWildlifeAuction(newWildlifeAuction);
        setLoading(false);
        if (response) {
          if (cacheItems.hasOwnProperty("wildlife_auctions")) {
            // Create a new object without the key
            const { wildlife_auctions, ...rest } = cacheItems; // Destructure to exclude key
            setCacheItems(rest); // Set the new cache items without key
          }
          const state = {
            color: "success",
            icon: "check",
            msg: "Auction Added Successfully!",
            title: "Auction Listing",
          };
          navigate("/active-listings", { state });
        }
      } catch (res) {
        if (res.hasOwnProperty("message")) {
          openAlertSB({ color: "error", icon: "warning", msg: res.message, title: "Auction Listing" });
          setTimeout(function () {
            closeAlertSB();
          }, 4000);
        } else {
          openAlertSB({
            color: "error",
            icon: "warning",
            msg: res.errors[0].detail,
            title: "Auction Listing",
          });
          setTimeout(function () {
            closeAlertSB();
          }, 4000);
        }
        setLoading(false);
      }
    } else {
      try {
        const response = await WildlifeAuctionService.updateWildlifeAuction(id, newWildlifeAuction);
        setLoading(false);
        if (response) {
          if (cacheItems.hasOwnProperty("wildlife_auctions")) {
            // Create a new object without the key
            const { wildlife_auctions, ...rest } = cacheItems; // Destructure to exclude key
            setCacheItems(rest); // Set the new cache items without key
          }
          openAlertSB({
            color: "success",
            icon: "check",
            msg: "Listing Updated Successfully!",
            title: "Auction Listing",
          });
          setTimeout(function () {
            closeAlertSB();
          }, 4000);
        }
      } catch (res) {
        if (res.hasOwnProperty("message")) {
          openAlertSB({ color: "error", icon: "warning", msg: res.message, title: "Auction Listing" });
          setTimeout(function () {
            closeAlertSB();
          }, 4000);
        } else {
          openAlertSB({
            color: "error",
            icon: "warning",
            msg: res.errors[0].detail,
            title: "Auction Listing",
          });
          setTimeout(function () {
            closeAlertSB();
          }, 4000);
        }
        setLoading(false);
      }
    }

    return () => {
      setInputs({
        title: "",
      });

      setErrors({
        listingIdError: false,
        listingUrlError: false,
        imageUrlError: false,
        titleError: false,
        subtitleError: false,
        sellerError: false,
        listingTypeError: false,
        priceError: false,
        categoryError: false,
        speciesError: false,
        reserveStatusError: false,
        statusError: false,
      });
    };
  };

  return (
    <>
      <DashboardLayout
        breadcrumbsTitle={action === "add" ? "Add Listing" : `Edit Listing : ${headerTitle}`}
        isNotFound={inputs?.id && inputs?.id > 0 || action === 'add' ? false : true}
        isData={isData}
      >
        <MDBox py={3}>
          <MDBox component="form" role="form" method="POST" onSubmit={submitHandler}>
            <Grid container>
              <Grid item xs={12} md={5} px={1}>
                <Paper elevation={3} sx={{ p: 3, mb: 4, borderLeft: `6px solid ${theme.palette.info.main}` }}>
                  <Grid container>
                    <Grid item xs={12} md={5} pb={2}>
                      <MDTypography variant="h5" gutterBottom>
                        Basic Details
                      </MDTypography>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item md={4} xs={12} px={3}>
                      <MDBox mb={2}
                        onClick={() => {
                          if (image) {
                            openCarousel(images)
                          }
                        }}
                        sx={{
                          width: 'auto',
                          height: 150,
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={image ? image : noImage}
                          alt="Avatar"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      </MDBox>
                    </Grid>
                    <Grid item md={8} xs={12} px={3}>
                      <Grid container>
                        <Grid item xs={12} md={12}>
                          <MDBox mb={2} display="flex" style={{ whiteSpace: "nowrap" }} alignItems="center" color="error">
                            <MDTypography variant="button" fontWeight="medium" mr={1}>
                              Remaining Time:
                            </MDTypography>
                            {endDate ? (
                              <MDTimeTicker endDateTime={endDate} />
                            ) : ''}
                          </MDBox>
                        </Grid>
                        <Grid item md={12} xs={12}>
                          <MDBox mb={2}>
                            <MDSelect
                              label="Source"
                              fullWidth
                              name="source"
                              value={inputs.source}
                              onChange={changeHandler}
                              options={sourceOptions}
                              required
                              helperText={errors.sourceError ? errors.sourceError : ''}
                            />
                          </MDBox>
                        </Grid>
                        <Grid item md={12} xs={12}>
                          <MDBox mb={2}>
                            <MDInput
                              label="Listing Id"
                              fullWidth
                              name="listing_id"
                              value={inputs.listing_id}
                              onChange={changeHandler}
                              required
                              error={errors.listingIdError}
                              helperText={errors.listingIdError ? errors.listingIdError : ''}
                            />
                          </MDBox>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={12} px={3}>
                      <MDBox mb={2}>
                        <MDInput
                          type="text"
                          label="Title"
                          fullWidth
                          value={inputs.title}
                          name="title"
                          inputProps={{ maxLength: 255 }}
                          onChange={changeHandler}
                          error={errors.titleError}
                          helperText={errors.titleError ? errors.titleError : ''}
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={12} px={3}>
                      <MDBox mb={2}>
                        <MDInput
                          type="text"
                          label="Listing Url"
                          fullWidth
                          value={inputs.listing_url}
                          name="listing_url"
                          inputProps={{ maxLength: 256 }}
                          onChange={changeHandler}
                          error={errors.listingUrlError}
                          helperText={errors.listingUrlError ? errors.listingUrlError : ''}
                          required
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={12} px={3}>
                      <MDBox mb={2}>
                        <MDInput
                          type="text"
                          label="Image Url"
                          fullWidth
                          value={inputs.image_url}
                          name="image_url"
                          inputProps={{ maxLength: 200 }}
                          onChange={changeHandler}
                          error={errors.imageUrlError}
                          helperText={errors.imageUrlError ? errors.imageUrlError : ''}
                          required
                        />
                      </MDBox>
                    </Grid>

                    <Grid item xs={12} md={12} px={3}>
                      <MDBox mb={2}>
                        <MDInput
                          type="text"
                          label="Sub Title"
                          fullWidth
                          value={inputs.subtitle}
                          name="subtitle"
                          inputProps={{ maxLength: 255 }}
                          onChange={changeHandler}
                          error={errors.subtitleError}
                          helperText={errors.subtitleError ? errors.subtitleError : ''}
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6} px={3}>
                      <MDBox mb={2}>
                        <MDInput
                          type="text"
                          label="Seller"
                          fullWidth
                          value={inputs.seller}
                          name="seller"
                          inputProps={{ maxLength: 100 }}
                          onChange={changeHandler}
                          error={errors.sellerError}
                          helperText={errors.sellerError ? errors.sellerError : ''}
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6} px={3}>
                      <MDBox mb={2}>
                        <MDInput
                          type="text"
                          label="Listing Type"
                          fullWidth
                          value={inputs.listing_type}
                          name="listing_type"
                          inputProps={{ maxLength: 50 }}
                          onChange={changeHandler}
                          error={errors.listingTypeError}
                          helperText={errors.listingTypeError ? errors.listingTypeError : ''}
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6} px={3}>
                      <MDBox mb={2}>
                        <MDInput
                          type="text"
                          label="Price"
                          fullWidth
                          value={inputs.price}
                          name="price"
                          inputProps={{ maxLength: 13 }}
                          onChange={changeHandler}
                          error={errors.priceError}
                          helperText={errors.priceError ? errors.priceError : ''}
                          required
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6} px={3}>
                      <MDBox mb={2}>
                        <MDInput
                          type="text"
                          label="Category"
                          fullWidth
                          value={inputs.category}
                          name="category"
                          inputProps={{ maxLength: 100 }}
                          onChange={changeHandler}
                          error={errors.categoryError}
                          helperText={errors.categoryError ? errors.categoryError : ''}
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6} px={3}>
                      <MDBox mb={2}>
                        <MDInput
                          type="text"
                          label="Species"
                          fullWidth
                          value={inputs.species}
                          name="species"
                          inputProps={{ maxLength: 100 }}
                          onChange={changeHandler}
                          error={errors.speciesError}
                          helperText={errors.speciesError ? errors.speciesError : ''}
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6} px={3}>
                      <MDBox mb={2}>
                        <MDInput
                          type="text"
                          label="Reserve Status"
                          fullWidth
                          value={inputs.reserve_status}
                          name="reserve_status"
                          inputProps={{ maxLength: 100 }}
                          onChange={changeHandler}
                          error={errors.reserveStatusError}
                          helperText={errors.reserveStatusError ? errors.reserveStatusError : ''}
                        />
                      </MDBox>
                    </Grid>
                    <Grid item md={6} xs={12} px={3}>
                      <MDBox mb={2}>
                        <MDDateTimePicker
                          label="Start Date"
                          name="from_date"
                          value={dayjs(startDate).isValid() ? dayjs(startDate) : null}
                          format="DD-MM-YYYY h:m:s a"
                          onChange={(newValue) => {
                            setStartDate(
                              dayjs(newValue).isValid() ? dayjs(newValue).format("YYYY-MM-DD H:m:s") : null
                            );
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                            },
                            field: {
                              clearable: true,
                            },
                            textField: { size: "small" },
                          }}
                        />
                      </MDBox>
                    </Grid>
                    <Grid item md={6} xs={12} px={3}>
                      <MDBox mb={2}>
                        <MDDateTimePicker
                          label="End Date"
                          name="end_date"
                          value={dayjs(endDate).isValid() ? dayjs(endDate) : null}
                          format="DD-MM-YYYY h:m:s a"
                          onChange={(newValue) => {
                            setEndDate(
                              dayjs(newValue).isValid() ? dayjs(newValue).format("YYYY-MM-DD H:m:s") : null
                            );
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                            },
                            field: {
                              clearable: true,
                            },
                            textField: { size: "small" },
                          }}
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6} px={3}>
                      <MDBox mb={2}>
                        <MDInput
                          type="text"
                          label="Status"
                          fullWidth
                          value={inputs.status}
                          name="status"
                          inputProps={{ maxLength: 50 }}
                          onChange={changeHandler}
                          error={errors.statusError}
                          helperText={errors.statusError ? errors.statusError : ''}
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={3} px={3}>
                      <MDBox mb={2}>
                        <MDInput
                          type="number"
                          label="Male"
                          fullWidth
                          value={inputs.male}
                          name="male"
                          inputProps={{ min: 0, max: 50 }}
                          onChange={changeHandler}
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={3} px={3}>
                      <MDBox mb={2}>
                        <MDInput
                          type="number"
                          label="Female"
                          fullWidth
                          value={inputs.female}
                          name="female"
                          inputProps={{ min: 0, max: 50 }}
                          onChange={changeHandler}
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={3} px={3}>
                      <MDBox mb={2}>
                        <MDInput
                          type="number"
                          label="Male Calf"
                          fullWidth
                          value={inputs.male_calf}
                          name="male_calf"
                          inputProps={{ min: 0, max: 50 }}
                          onChange={changeHandler}
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={3} px={3}>
                      <MDBox mb={2}>
                        <MDInput
                          type="number"
                          label="Female"
                          fullWidth
                          value={inputs.female_calf}
                          name="female_calf"
                          inputProps={{ min: 0, max: 50 }}
                          onChange={changeHandler}
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={3} px={3}>
                      <MDBox mb={2}>
                        <MDInput
                          type="number"
                          label="Unsexed Calf"
                          fullWidth
                          value={inputs.unsexed_calf}
                          name="unsexed_calf"
                          inputProps={{ min: 0, max: 50 }}
                          onChange={changeHandler}
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={3} px={3}>
                      <MDBox mb={2}>
                        <MDSwitch
                          checked={!!active}
                          onChange={() => setActive((prev) => !prev)}
                          label="Active"
                          inputProps={{ "aria-label": "controlled" }}
                          color="info"
                        />
                      </MDBox>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item container xs={12} md={7} px={1}>
                <Paper elevation={3} sx={{ p: 3, mb: 4, borderLeft: `6px solid ${theme.palette.success.main}` }}>
                  <Grid item container>
                    <Grid item xs={12} md={12} pb={3}>
                      <MDTypography variant="h5">
                        Description
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12} md={12} pb={2}>
                      {descriptionLoading ? (
                        <MDBox
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          marginTop={15}
                        >
                          <MDCircularProgress
                            size={80}
                            color="success"
                          />
                        </MDBox>
                      ) : (<MDXEditorComponent
                        value={description}
                        name="description"
                        onChange={handleEditorChange}
                        placeholder="Description"
                      />)}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
            <Grid container mt={2} justifyContent="flex-end">
              <Grid item md={10} xs={1}></Grid>
              <Grid item md={1} xs={3}>
                <MDBox mr={2}>
                  <MDButton component={Link} to={inputs.active == 0 ? "/closed-listings" : "/active-listings"} variant="gradient" color="primary" fullWidth>
                    Back
                  </MDButton>
                </MDBox>
              </Grid>
              <Grid item md={1} xs={3}>
                <MDBox mr={3}>
                  <MDButton variant="gradient" color="info" fullWidth type="submit" disabled={loading}>
                    {loading ? (<MDCircularProgress
                      size={24}
                      sx={{
                        position: "absolute",
                      }}
                    />
                    ) : action === "add" ? (
                      "Save"
                    ) : (
                      "Update"
                    )}
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </MDBox>
      </DashboardLayout>
      {renderAlertSB}
    </>
  );
}

export default Form;
